import { Flex, Text, UnstyledButton, rem } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { flagIconFromAlpha2 } from "@/shared/utils/countryFlagEmoji";
import type { DiscoverPromoSlide } from "@shared/discoverPromoSlide";

export type { DiscoverPromoSlide } from "@shared/discoverPromoSlide";

export const NAVY = "#001f3f";
export const MUTED_TEXT = "#6b7280";
export const CTA_BG = "#ffc107";
export const LINE_GRAY = "#e5e7eb";

/** Horizontal padding inside slides when prev/next controls are shown (control + offset + buffer). */
export const SLIDE_INSET_WITH_CONTROLS = rem(50);

export type DiscoverPromoCarouselBaseProps = {
  slides: DiscoverPromoSlide[];
  height?: number;
  autoplayDelayMs?: number;
};

export function DiscoverPromoSlideCard({
  slide,
  multi,
  compact = false,
}: {
  slide: DiscoverPromoSlide;
  multi: boolean;
  compact?: boolean;
}) {
  const codes = slide.flags?.map((c) => c.toUpperCase()).filter(Boolean) ?? [];

  return (
    <Flex
      w="100%"
      maw="100%"
      mih={0}
      h="100%"
      direction="column"
      justify="center"
      align="center"
      gap={compact ? rem(2) : "sm"}
      px={multi ? (compact ? rem(36) : SLIDE_INSET_WITH_CONTROLS) : compact ? rem(8) : rem(12)}
      py={compact ? rem(2) : rem(10)}
      bg={slide.surfaceBg ?? "var(--mantine-color-body)"}
      style={{
        borderRadius: "var(--mantine-radius-md)",
        boxSizing: "border-box",
      }}
    >
      <Flex w="100%" maw="100%" miw={0} direction="column" align="center" gap="xs">
        {codes.length > 0 ? (
          <Flex gap={10} justify="center" align="center" wrap="nowrap">
            {codes.map((code) => {
              const flag = flagIconFromAlpha2(code);
              if (!flag) return null;
              return (
                <Text key={code} span fz={compact ? 20 : 24} lh={1} role="img" aria-label={`${code} flag`}>
                  {flag}
                </Text>
              );
            })}
          </Flex>
        ) : null}
        <Text
          fw={700}
          size="sm"
          c={slide.headlineColor ?? "primary.7"}
          ta="center"
          maw="100%"
          lineClamp={3}
          style={{ wordBreak: "break-word" }}
        >
          {slide.headline}
        </Text>
        <Text
          size="xs"
          c={slide.descriptionColor ?? "slate.6"}
          lh={1.55}
          ta="center"
          maw="100%"
          style={{ wordBreak: "break-word" }}
        >
          {slide.description}
        </Text>
      </Flex>
    </Flex>
  );
}

/** Mobile/tablet: flags above headline, description below. */
export function DiscoverPromoSlideCardMobile({ slide }: { slide: DiscoverPromoSlide }) {
  const codes = slide.flags?.map((c) => c.toUpperCase()).filter(Boolean) ?? [];

  return (
    <Flex
      w="100%"
      h="100%"
      direction="column"
      justify="center"
      align="center"
      miw={0}
      py={rem(8)}
      px={rem(30)}
      bg="#fff"
      style={{ boxSizing: "border-box" }}
    >
      {codes.length > 0 ? (
        <Flex gap={8} justify="center" align="center" wrap="nowrap" mb={rem(4)}>
          {codes.map((code) => {
            const flag = flagIconFromAlpha2(code);
            if (!flag) return null;
            return (
              <Text key={code} span fz={rem(18)} lh={1} role="img" aria-label={`${code} flag`}>
                {flag}
              </Text>
            );
          })}
        </Flex>
      ) : null}
      <Text
        fw={700}
        fz={rem(12)}
        lh={1.35}
        c={NAVY}
        ta="center"
        maw="100%"
        lineClamp={2}
        style={{ wordBreak: "break-word" }}
      >
        {slide.headline}
      </Text>
      <Text
        fz={rem(10)}
        mt={rem(2)}
        c={MUTED_TEXT}
        ta="center"
        maw="100%"
        lineClamp={2}
        style={{ wordBreak: "break-word" }}
      >
        {slide.description}
      </Text>
    </Flex>
  );
}

export function CarouselNavButton({
  direction,
  onClick,
  disabled,
  label,
}: {
  direction: "prev" | "next";
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  const Icon = direction === "prev" ? IconChevronLeft : IconChevronRight;
  return (
    <UnstyledButton
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      w={rem(26)}
      h={rem(26)}
      style={{
        flexShrink: 0,
        borderRadius: "50%",
        border: `1px solid ${LINE_GRAY}`,
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.35 : 1,
        pointerEvents: disabled ? "none" : "auto",
        color: "#9ca3af",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Icon size={13} stroke={2} />
    </UnstyledButton>
  );
}
